import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../AuthContextWrapper";
import { Box, Button, Typography } from "@mui/material";

import chill1 from "../../assets/chill-1x.png";
import chill15 from "../../assets/chill-1.5x.png";
import chill2 from "../../assets/chill-2x.png";
import chill3 from "../../assets/chill-3x.png";

import { Estate } from "../../../models/Estate";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";

export enum Outcomes {
  getStarted = "Get Started",
  alreadyStarted = "I already started probate",
}

export const WelcomeScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, setActiveEstate, user } = useContext(AuthContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (answer: string): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    const estate = activeEstate || new Estate();
    if (user?.profile?.countryId) {
      // preset countryId
      const countryId = parseInt(user.profile.countryId, 10);
      if (!isNaN(countryId)) {
        estate.countryId = countryId;
      }
    }
    if (user?.profile?.stateId) {
      // in some cases the stateId will be selected during sign up and can immediately be set on the estate here
      const stateId = parseInt(user.profile.stateId, 10);
      if (!isNaN(stateId)) {
        estate.stateId = stateId;
      }
    }
    estate.alreadyStarted = answer === Outcomes.alreadyStarted;

    try {
      await estate.saveEstate();
      setActiveEstate && setActiveEstate(estate);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, answer, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper hideSectionHeading={true} centerContent={true}>
      <>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <img
            srcSet={`${chill15} 1.5x, ${chill2} 2x, ${chill3} 3x`}
            src={chill1}
            alt="Chill"
            title="Chill"
          />
        </Box>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <Typography component="h2" variant="h3">
            Welcome to the Atticus family{user?.first ? `, ${user?.first}` : ""}
            !
          </Typography>
          <Typography
            variant="body1"
            sx={{
              my: 2,
              mx: "auto",
              width: "80%",
              maxWidth: 480,
            }}
          >
            We’re going to ask you a few simple questions to help us understand
            where you’re at in the process.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            my: 6,
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleComplete(Outcomes.getStarted)}
            sx={{
              minWidth: 180,
              mb: 3,
            }}
          >
            {Outcomes.getStarted}
          </Button>
          <Button
            variant="text"
            onClick={() => handleComplete(Outcomes.alreadyStarted)}
          >
            {Outcomes.alreadyStarted}
          </Button>
        </Box>
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
